import type { Animal } from "../animals";

// A football jersey (back view) — the MBAPPÉ #10 shirt. The user supplied the
// step-by-step outline strokes (from the SVG step editor); we keep them exactly
// as drawn and add a body fill so kids can colour the shirt, then the "write the
// name" finale.
const OUTLINE = "#2a2350";
// The shirt colour (filled in the last "colour it in" step).
const SHIRT = "#2f5fd0";
// The red trim — cuff bands and side panels, France style.
const RED = "#ce1126";

// Red cuff bands: the region between the cuff seam stroke and the sleeve
// opening edge, both traced from the outline strokes below.
const CUFF_L =
  "M 32.5,67.4 Q 36.3,68.4 43.9,73.2 Q 51.4,78 54.6,78.9 L 57.7,79.9 " +
  "L 62.7,83.5 Q 52.7,85.5 41.8,80 L 30.9,74.4 Z";
const CUFF_R = "M 146.1,79.2 L 169.5,66 L 172.7,72.1 L 160.2,79.6 Q 147.7,87.1 140.9,83.5 Z";
// Red side panels: tapered crescents hugging the body edges from the armpits
// down to the side-seam strokes (which then read as seams over the red).
const SIDE_L =
  "M 63.8,86 Q 68.2,104 69.3,124 Q 69.9,138 70.7,147.6 L 68,163.9 " +
  "Q 64.5,161 62.2,156 Q 62.6,146 62.7,138 Q 64.4,112 64.3,98 Q 64.1,90 63.8,86 Z";
const SIDE_R =
  "M 139,87 Q 135.2,105 133.9,125 Q 133.1,138 132.3,146.9 L 137.3,162.4 " +
  "Q 141.2,159 141.5,151.7 Q 137.8,123.3 138.4,102.8 Q 138.7,93 139,87 Z";

// The shirt silhouette, traced round the outline strokes below (left sleeve tip
// → collar → right sleeve tip → down the sides → hem → back up) so the colour
// fills the whole shirt. Drawn behind the strokes, so the "10" and name stay on
// top. The right-shoulder edge mirrors the left across x=100.
const BODY =
  "M 31.8,73 Q 30.7,69.6 32.7,64.4 Q 34.7,59.3 40,49.8 Q 45.3,40.3 48.6,35.5 " +
  "Q 51.8,30.8 57.7,26.4 Q 63.6,22 73.9,17.3 L 84.3,12.6 " +
  "Q 89,8.3 90,8 Q 91,7.7 101.2,7.2 Q 111.4,6.7 113.3,7.4 Q 115.2,8.1 115.9,10.1 L 116.6,12.1 " +
  "L 118.2,12 Q 119.7,13.6 127.9,16.3 Q 136.1,18.9 142.8,23 Q 149.4,27.1 152,30.7 " +
  "Q 154.7,34.2 159.5,44.3 Q 164.4,54.4 168.8,61.4 Q 173.2,68.5 173.9,72.4 " +
  "L 172.7,72.1 L 160.2,79.6 Q 147.7,87.1 140.9,83.5 " +
  "L 138.4,102.8 Q 137.8,123.3 141.5,151.7 L 145.2,180.1 " +
  "L 144.3,181.7 L 134.5,184.4 Q 124.7,187.2 107.4,187.8 Q 90,188.4 79.1,187.3 Q 68.2,186.1 58.9,181 " +
  "L 60,181 L 62.5,138.2 Q 65.1,95.4 62.7,75.3 " +
  "L 62.7,83.5 Q 52.7,85.5 41.8,80 L 30.9,74.4 Z";

// The shirt body with the red-trim shapes punched out as even-odd holes. The
// child colours the red trim FIRST; the blue body is then drawn with those same
// shapes cut out, so filling the shirt blue leaves the already-coloured red
// showing through instead of painting over it (z-order follows colouring order).
const BODY_HOLED = [BODY, CUFF_L, CUFF_R, SIDE_L, SIDE_R].join(" ");

// The white "10" and "MBAPPÉ". These are painted over the pink shirt but behind
// the navy outline strokes (fills always render behind strokes), so number and
// name read as solid white glyphs with a navy edge — like a real shirt. White is
// "paper" to expandColorSteps, so in the colour-in step they appear as the canvas
// showing through the shirt. Holes (the 0's ring, the counters of B/A/P) are cut
// with even-odd. The 1 and É are drawn as separate outline segments, so their
// fills are single hand-traced silhouettes rather than a re-use of the strokes.
const WHITE = "#ffffff";
const ZERO_FILL =
  "M 98.9,100.8 Q 98.4,69.6 98.8,66.6 Q 99.1,63.5 100.1,60.4 Q 101.1,57.3 102.2,55.2 Q 103.3,53.2 105.1,51.8 Q 107,50.4 110.2,49.7 Q 113.4,48.9 116.5,49.7 Q 119.7,50.4 120.6,51 Q 121.4,51.6 122.4,53.5 Q 123.3,55.3 123.8,62.8 Q 124.2,70.3 124.2,81.8 Q 124.3,93.4 123.3,97.8 Q 122.3,102.1 120.5,104.6 Q 118.6,107.1 114.9,108.6 Q 111.1,110.1 108.3,108.9 Q 105.5,107.7 102.6,104.5 L 99.8,101.2 Z " +
  "M 111.8,57.8 Q 110,57.5 109.2,58 Q 108.4,58.4 107,61.4 Q 105.6,64.4 105.3,76.3 Q 105.1,88.2 105.6,92.4 Q 106.2,96.7 107.1,98.5 Q 108,100.3 109.9,100.9 Q 111.8,101.5 113.8,100.8 Q 115.8,100.1 116.6,98.2 Q 117.4,96.2 118.2,89.5 Q 119,82.9 118.6,74.4 Q 118.2,66 117.8,63.9 Q 117.3,61.7 116.6,60.9 Q 116,60 113.8,59 L 111.6,58 Z";
const ONE_FILL =
  "M 74.5,55.3 L 83.6,50.5 L 84.5,49.4 L 91.1,49.2 L 95,54.9 Q 93.9,61.2 93.6,76 Q 93.3,90.9 94,97.9 Q 94.8,105 94.2,107.2 L 93.6,109.4 L 92.5,112.4 L 86.4,112.6 L 83.9,110.1 L 84.3,107.4 Q 83.4,90.5 83.7,84.2 Q 83.5,70.3 84.5,58.7 L 81.4,59.4 L 74.5,64.9 L 74.8,63.7 Q 73.8,59.6 74.2,57.5 Z";
const M_FILL =
  "M 76.1,44.5 Q 75.9,42.3 75.9,41.2 Q 75.9,40.1 75.9,38.9 Q 75.9,37.8 75.9,36.7 Q 75.9,35.7 75.9,34.4 Q 75.9,33.1 76.1,32 Q 76.4,31 77.3,31.4 Q 78.2,31.9 78.6,32.9 Q 78.9,33.8 79.4,34.8 Q 79.8,35.8 80.9,35.8 Q 82,35.8 82.3,34.8 Q 82.7,33.7 83.3,32.9 Q 83.9,32 84.8,32.6 Q 85.6,33.2 85.6,34.4 Q 85.6,35.5 85.8,36.6 Q 85.9,37.6 85.8,38.7 Q 85.8,39.8 85.7,40.8 Q 85.6,41.9 84.6,42.3 Q 83.6,42.6 83.6,41.4 Q 83.5,40.2 83.6,39.1 Q 83.6,38.1 83,38.9 Q 82.3,39.8 81.7,40.6 Q 81.1,41.4 80.6,40.4 Q 80.2,39.3 79.8,38.3 Q 79.5,37.3 79.2,38.3 Q 78.9,39.3 78.9,40.4 Q 78.9,41.4 78.9,42.5 Q 78.9,43.5 77.9,43.6 L 76.8,43.7 Z";
const B_FILL =
  "M 88,44 Q 87.6,42 87.5,40.9 Q 87.4,39.9 87.4,38.9 Q 87.5,37.8 87.6,36.8 Q 87.8,35.8 88,34.7 Q 88.2,33.6 88.7,32.7 Q 89.3,31.9 90.3,31.6 Q 91.2,31.3 92.2,31.8 Q 93.2,32.3 93.9,33.1 Q 94.5,34 94.5,35 Q 94.5,36 93.7,36.6 Q 92.9,37.2 93.2,38.2 Q 93.5,39.2 93.8,40.2 Q 94.1,41.1 93.5,42 Q 92.8,42.8 91.9,43.3 Q 91,43.9 90,44.1 L 89,44.4 Z " +
  "M 90.7,34.2 Q 89.9,36.2 90.9,36.6 Q 91.8,37 91.7,36 L 91.6,35 Z " +
  "M 90.6,38.9 Q 88.7,39.5 88.8,40.6 Q 89,41.7 89.9,42.2 Q 90.8,42.7 91.2,41.7 Q 91.6,40.7 91.2,39.8 L 90.7,38.9 Z";
const A_FILL =
  "M 94.6,44 Q 94.9,41.7 95.2,40.5 Q 95.5,39.3 95.7,38.2 Q 96,37.1 96.3,36.1 Q 96.6,35.2 97.1,34.2 Q 97.6,33.3 98.5,33 Q 99.5,32.7 99.7,33.7 Q 100,34.7 100.3,35.7 Q 100.6,36.7 100.9,37.7 Q 101.2,38.6 101.4,39.6 Q 101.5,40.6 101.6,41.7 L 101.6,42.7 " +
  "M 94.7,43.9 Q 96.8,43.9 96.7,42.9 Q 96.7,41.9 97.7,41.9 Q 98.7,41.9 99.1,42.8 Q 99.6,43.7 100.6,44 L 101.5,44.3 Z " +
  "M 97.1,40 Q 97.6,38 97.9,37.1 Q 98.3,36.1 98.9,36.9 Q 99.5,37.7 99.6,38.8 Q 99.7,39.8 98.7,39.6 L 97.7,39.4 Z";
const P1_FILL =
  "M 103.3,44.6 Q 103.5,42.6 103.5,41.6 Q 103.4,40.5 103.4,39.5 Q 103.3,38.4 103.2,37.4 Q 103,36.4 103,35.4 Q 103,34.4 103.3,33.4 Q 103.6,32.4 104.6,32.1 Q 105.7,31.9 106.7,32.2 Q 107.7,32.5 108.5,33.2 Q 109.3,33.9 109.4,34.9 Q 109.5,35.9 109.1,36.9 Q 108.8,37.8 108,38.5 Q 107.3,39.2 106.7,40.1 Q 106.1,40.9 106,42 Q 106,43 105.9,44 L 105.9,45 Z " +
  "M 105.6,34.3 Q 104.6,36.1 104.8,37.1 Q 104.9,38.1 105.9,37.8 Q 106.9,37.5 106.8,36.5 L 106.8,35.5 Z";
const P2_FILL =
  "M 110.9,44.6 Q 110.6,42.6 110.5,41.4 Q 110.4,40.2 110.5,39.1 Q 110.5,38 110.6,37 Q 110.6,35.9 110.7,34.9 Q 110.7,33.9 111.4,33.1 Q 112,32.4 113,32.3 Q 114,32.2 115,32.6 Q 116,33.1 116.5,34 Q 117,34.9 116.8,35.9 Q 116.7,36.9 116.1,37.8 Q 115.6,38.7 114.5,39 Q 113.5,39.2 113.5,40.3 Q 113.5,41.4 113.5,42.4 Q 113.4,43.4 112.7,44.1 L 111.9,44.8 Z " +
  "M 113.4,34.3 Q 112.4,36.2 112.6,37.2 Q 112.8,38.2 113.6,37.6 Q 114.4,36.9 114.2,35.9 L 114,34.9 Z";
const E_FILL =
  "M 118.4,32.1 L 121.5,32 L 122.4,33.2 L 121.3,34.4 L 120.2,36.8 L 120.7,36.6 L 121.7,36.8 L 123.1,38.9 L 122.5,38.9 L 120.7,40.8 L 120.6,41.5 L 122,41.6 L 123,41.8 L 124,42.1 L 123.9,44.1 L 120.5,44.4 L 118.9,43.9 L 118.3,42.1 Q 118.4,39 118.5,38 Q 118.6,35 118.6,32.8 Z";

export const jersey: Animal = {
  id: "jersey",
  name: "Jersey",
  emoji: "👕",
  viewBox: "0 0 200 200",
  color: OUTLINE,
  level: 10,
  steps: [
    {
      hint: "Draw the two sides and the bottom of the shirt 👕",
      color: OUTLINE,
      strokeWidth: 0.5,
      strokes: [
        "M 51.4,32.6 Q 60.2,55.1 62.7,75.3 Q 65.1,95.4 62.5,138.2 L 60,181",
        "M 153.6,31.2 Q 146.1,45.8 142.5,64 Q 139,82.2 138.4,102.8 Q 137.8,123.3 141.5,151.7 L 145.2,180.1",
        "M 58.9,181 Q 68.2,186.1 79.1,187.3 Q 90,188.4 107.4,187.8 Q 124.7,187.2 134.5,184.4 L 144.3,181.7",
      ],
    },
    {
      hint: "Add the underarm lines and the side seams 🧵",
      color: OUTLINE,
      strokeWidth: 0.5,
      strokes: [
        "M 154.5,71.9",
        "M 62.7,83.5 Q 52.7,85.5 41.8,80 L 30.9,74.4",
        "M 140.9,83.5 Q 147.7,87.1 160.2,79.6 L 172.7,72.1",
        "M 138.4,128.3 L 132.3,146.9",
        "M 140,151.9 Q 139.5,156.4 138.4,159.4 L 137.3,162.4",
        "M 61.4,150.3 Q 64.1,158.7 66,161.3 L 68,163.9",
        "M 64.3,127.1 Q 65.3,133.5 66.6,136.6 Q 68,139.7 68.7,142.9 Q 69.4,146 70,146.8 L 70.7,147.6",
      ],
    },
    {
      hint: "Draw the shoulders and the sleeves 💪",
      color: OUTLINE,
      strokeWidth: 0.5,
      strokes: [
        "M 31.8,73 Q 30.7,69.6 32.7,64.4 Q 34.7,59.3 40,49.8 Q 45.3,40.3 48.6,35.5 Q 51.8,30.8 57.7,26.4 Q 63.6,22 73.9,17.3 L 84.3,12.6",
        "M 173.9,72.4 Q 173.2,68.5 168.8,61.4 Q 164.4,54.4 159.5,44.3 Q 154.7,34.2 152,30.7 Q 149.4,27.1 142.8,23 Q 136.1,18.9 127.9,16.3 Q 119.7,13.6 118.2,12 L 116.6,10.5",
      ],
    },
    {
      hint: "Add the collar at the neck 🧵",
      color: OUTLINE,
      strokeWidth: 0.5,
      strokes: [
        "M 86.4,12.8 Q 107.5,13.8 112.4,13 L 117.3,12.1",
        "M 84.1,13.3 Q 89,8.3 90,8 Q 91,7.7 101.2,7.2 Q 111.4,6.7 113.3,7.4 Q 115.2,8.1 115.9,10.1 L 116.6,12.1",
      ],
    },
    {
      hint: "Add the cuffs and the shoulder seams ✂️",
      color: OUTLINE,
      strokeWidth: 0.5,
      strokes: [
        "M 60.7,63.7 Q 58.5,76.2 56.8,80 L 55,83.7",
        "M 140.7,72.8",
        "M 141.4,72.4 Q 147.6,81.2 149.6,82.5 L 151.6,83.7",
        "M 146.1,79.2 L 169.5,66",
        "M 32.5,67.4 Q 36.3,68.4 43.9,73.2 Q 51.4,78 54.6,78.9 L 57.7,79.9",
        "M 148.4,59.4 Q 147.3,63.4 145.1,67.2 L 143,71",
        "M 157,51.7 L 153.6,63.9",
        "M 51.8,62.4 L 60.2,72.4",
        "M 41.8,57.4 Q 45,63 47.4,65.3 L 49.8,67.6",
      ],
    },
    {
      hint: "Draw the hem, then start the big number 🔢",
      color: OUTLINE,
      strokeWidth: 0.5,
      strokes: [
        "M 60.5,177.1 Q 64.7,177.4 72.3,179.4 Q 79.9,181.5 89,182.3 Q 98.2,183.2 113.3,181.9 Q 128.4,180.7 131.8,180.1 Q 135.3,179.5 138.4,178.2 L 141.6,176.9",
        "M 98.9,100.8 Q 98.4,69.6 98.8,66.6 Q 99.1,63.5 100.1,60.4 Q 101.1,57.3 102.2,55.2 Q 103.3,53.2 105.1,51.8 Q 107,50.4 110.2,49.7 Q 113.4,48.9 116.5,49.7 Q 119.7,50.4 120.6,51 Q 121.4,51.6 122.4,53.5 Q 123.3,55.3 123.8,62.8 Q 124.2,70.3 124.2,81.8 Q 124.3,93.4 123.3,97.8 Q 122.3,102.1 120.5,104.6 Q 118.6,107.1 114.9,108.6 Q 111.1,110.1 108.3,108.9 Q 105.5,107.7 102.6,104.5 L 99.8,101.2",
        "M 95,54.9 Q 93.9,61.2 93.6,76 Q 93.3,90.9 94,97.9 Q 94.8,105 94.2,107.2 L 93.6,109.4",
        "M 95.2,56 L 91.4,50.8",
        "M 91.8,50.5 Q 90.5,61.3 90.1,83.9 L 89.8,106.4",
        "M 90,107.1 L 92.7,110.3",
        "M 84.1,108.5 L 88.4,108",
        "M 92.5,112.4 L 86.4,112.6",
        "M 86.6,113.3 L 83.9,110.1",
        "M 84.5,58.7 Q 83.5,62.8 83.9,70.3 Q 84.3,77.8 83.8,84.2 Q 83.4,90.5 83.9,98.9 L 84.3,107.4",
        "M 79.1,67.4 L 83.9,63",
        "M 79.5,68 L 76.1,65.1",
        "M 74.5,64.9 L 81.4,59.4",
        "M 83.6,59.2",
        "M 84.1,59.2 L 82.5,57.8",
        "M 74.8,63.7 Q 73.8,59.6 74.2,57.5 L 74.5,55.3",
        "M 73.4,55.5 L 83.6,50.5",
        "M 84.5,49.4 L 91.1,49.2",
      ],
    },
    {
      hint: "Give the number its 3-D edge 🔢",
      color: OUTLINE,
      strokeWidth: 0.5,
      strokes: [
        "M 111.8,57.8 Q 110,57.5 109.2,58 Q 108.4,58.4 107,61.4 Q 105.6,64.4 105.3,76.3 Q 105.1,88.2 105.6,92.4 Q 106.2,96.7 107.1,98.5 Q 108,100.3 109.9,100.9 Q 111.8,101.5 113.8,100.8 Q 115.8,100.1 116.6,98.2 Q 117.4,96.2 118.2,89.5 Q 119,82.9 118.6,74.4 Q 118.2,66 117.8,63.9 Q 117.3,61.7 116.6,60.9 Q 116,60 113.8,59 L 111.6,58",
        "M 114.8,60.1 Q 111.3,62 110,65 Q 108.7,68 108.3,77.9 Q 108,87.8 108.4,94.3 L 108.9,100.8",
        "M 109.1,60.3 L 110.9,63.9",
        "M 123.6,54.9 Q 126.3,67.4 126.8,72.5 Q 127.2,77.6 127.1,88.9 Q 126.9,100.3 126.3,102.3 Q 125.7,104.3 124.5,106 Q 123.3,107.6 121.5,108.7 Q 119.8,109.8 115.7,110.9 Q 111.6,112 109.7,111.2 Q 107.8,110.4 104.1,106.3 L 100.5,102.1",
        "M 124.8,106.2 L 122,103.5",
      ],
    },
    {
      hint: "Write the player's name — MBAPPÉ ⚽",
      color: OUTLINE,
      strokeWidth: 0.5,
      strokes: [
        "M 76.1,44.5 Q 75.9,42.3 75.9,41.2 Q 75.9,40.1 75.9,38.9 Q 75.9,37.8 75.9,36.7 Q 75.9,35.7 75.9,34.4 Q 75.9,33.1 76.1,32 Q 76.4,31 77.3,31.4 Q 78.2,31.9 78.6,32.9 Q 78.9,33.8 79.4,34.8 Q 79.8,35.8 80.9,35.8 Q 82,35.8 82.3,34.8 Q 82.7,33.7 83.3,32.9 Q 83.9,32 84.8,32.6 Q 85.6,33.2 85.6,34.4 Q 85.6,35.5 85.8,36.6 Q 85.9,37.6 85.8,38.7 Q 85.8,39.8 85.7,40.8 Q 85.6,41.9 84.6,42.3 Q 83.6,42.6 83.6,41.4 Q 83.5,40.2 83.6,39.1 Q 83.6,38.1 83,38.9 Q 82.3,39.8 81.7,40.6 Q 81.1,41.4 80.6,40.4 Q 80.2,39.3 79.8,38.3 Q 79.5,37.3 79.2,38.3 Q 78.9,39.3 78.9,40.4 Q 78.9,41.4 78.9,42.5 Q 78.9,43.5 77.9,43.6 L 76.8,43.7",
        "M 88,44 Q 87.6,42 87.5,40.9 Q 87.4,39.9 87.4,38.9 Q 87.5,37.8 87.6,36.8 Q 87.8,35.8 88,34.7 Q 88.2,33.6 88.7,32.7 Q 89.3,31.9 90.3,31.6 Q 91.2,31.3 92.2,31.8 Q 93.2,32.3 93.9,33.1 Q 94.5,34 94.5,35 Q 94.5,36 93.7,36.6 Q 92.9,37.2 93.2,38.2 Q 93.5,39.2 93.8,40.2 Q 94.1,41.1 93.5,42 Q 92.8,42.8 91.9,43.3 Q 91,43.9 90,44.1 L 89,44.4",
        "M 90.7,34.2 Q 89.9,36.2 90.9,36.6 Q 91.8,37 91.7,36 L 91.6,35",
        "M 90.6,38.9 Q 88.7,39.5 88.8,40.6 Q 89,41.7 89.9,42.2 Q 90.8,42.7 91.2,41.7 Q 91.6,40.7 91.2,39.8 L 90.7,38.9",
        "M 94.6,44 Q 94.9,41.7 95.2,40.5 Q 95.5,39.3 95.7,38.2 Q 96,37.1 96.3,36.1 Q 96.6,35.2 97.1,34.2 Q 97.6,33.3 98.5,33 Q 99.5,32.7 99.7,33.7 Q 100,34.7 100.3,35.7 Q 100.6,36.7 100.9,37.7 Q 101.2,38.6 101.4,39.6 Q 101.5,40.6 101.6,41.7 L 101.6,42.7",
        "M 97.1,40 Q 97.6,38 97.9,37.1 Q 98.3,36.1 98.9,36.9 Q 99.5,37.7 99.6,38.8 Q 99.7,39.8 98.7,39.6 L 97.7,39.4",
        "M 95.2,44",
        "M 95.4,43.9",
        "M 94.7,43.9 Q 96.8,43.9 96.7,42.9 Q 96.7,41.9 97.7,41.9 Q 98.7,41.9 99.1,42.8 Q 99.6,43.7 100.6,44 L 101.5,44.3",
        "M 103.3,44.6 Q 103.5,42.6 103.5,41.6 Q 103.4,40.5 103.4,39.5 Q 103.3,38.4 103.2,37.4 Q 103,36.4 103,35.4 Q 103,34.4 103.3,33.4 Q 103.6,32.4 104.6,32.1 Q 105.7,31.9 106.7,32.2 Q 107.7,32.5 108.5,33.2 Q 109.3,33.9 109.4,34.9 Q 109.5,35.9 109.1,36.9 Q 108.8,37.8 108,38.5 Q 107.3,39.2 106.7,40.1 Q 106.1,40.9 106,42 Q 106,43 105.9,44 L 105.9,45",
        "M 105.6,45.2 L 103.5,45",
        "M 105.6,34.3 Q 104.6,36.1 104.8,37.1 Q 104.9,38.1 105.9,37.8 Q 106.9,37.5 106.8,36.5 L 106.8,35.5",
        "M 110.9,44.6 Q 110.6,42.6 110.5,41.4 Q 110.4,40.2 110.5,39.1 Q 110.5,38 110.6,37 Q 110.6,35.9 110.7,34.9 Q 110.7,33.9 111.4,33.1 Q 112,32.4 113,32.3 Q 114,32.2 115,32.6 Q 116,33.1 116.5,34 Q 117,34.9 116.8,35.9 Q 116.7,36.9 116.1,37.8 Q 115.6,38.7 114.5,39 Q 113.5,39.2 113.5,40.3 Q 113.5,41.4 113.5,42.4 Q 113.4,43.4 112.7,44.1 L 111.9,44.8",
        "M 113.4,34.3 Q 112.4,36.2 112.6,37.2 Q 112.8,38.2 113.6,37.6 Q 114.4,36.9 114.2,35.9 L 114,34.9",
        "M 135.9,29",
        "M 118.6,32.8 Q 118.6,34.9 118.6,35.9 Q 118.6,37 118.5,38 Q 118.4,39 118.3,40 Q 118.3,41 118.3,42.1 Q 118.3,43.1 118.9,43.9 Q 119.5,44.7 120.5,44.4 Q 121.5,44.2 122.5,43.9 L 123.5,43.7",
        "M 123.9,44.1 Q 124,42.1 123,41.8 L 122,41.6",
        "M 120.7,41.9",
        "M 143.5,32.9",
        "M 122.6,41.6 L 120.6,41.5",
        "M 120.7,40.8 Q 120.5,38.8 121.5,38.9 L 122.5,38.9",
        "M 123.1,38.9 Q 122.7,36.9 121.7,36.8 L 120.7,36.6",
        "M 120.2,36.8 Q 120.3,34.7 121.3,34.4 Q 122.3,34.2 122.4,33.2 Q 122.5,32.2 121.5,32 Q 120.5,31.9 119.4,32 L 118.4,32.1",
      ],
    },
    {
      hint: "Colour the red trim first! 🖍️",
      color: OUTLINE,
      strokes: [],
      fills: [
        // Red trim FIRST, then the blue body (with the red shapes cut out as
        // holes) so the blue fills around the red instead of over it.
        { d: CUFF_L, color: RED },
        { d: CUFF_R, color: RED },
        { d: SIDE_L, color: RED },
        { d: SIDE_R, color: RED },
        { d: BODY_HOLED, color: SHIRT, evenodd: true, hint: "Now colour the shirt blue! 🖍️" },
        { d: ONE_FILL, color: WHITE, evenodd: true },
        { d: ZERO_FILL, color: WHITE, evenodd: true },
        { d: M_FILL, color: WHITE, evenodd: true },
        { d: B_FILL, color: WHITE, evenodd: true },
        { d: A_FILL, color: WHITE, evenodd: true },
        { d: P1_FILL, color: WHITE, evenodd: true },
        { d: P2_FILL, color: WHITE, evenodd: true },
        { d: E_FILL, color: WHITE, evenodd: true },
      ],
    },
  ],
};
